import { useEffect, useState, FC, PropsWithChildren, ReactNode } from 'react'
import { renderToString } from 'react-dom/server'
import { Provider } from 'react-redux'
import { Router, Switch } from 'react-router-dom'
import { ThemeProvider, ServerStyleSheet } from 'styled-components'
// @loadable/server is server-only. The server build inlines
// `@loadable-stats` via a resolve alias to public/assets/loadable-stats.json
// (mini_racer has no fs). The client build resolves both `@loadable/server`
// and `@loadable-stats` to empty modules via `resolve.alias: false`, so
// the imports below are `undefined` on the client and only the
// `if (serverSide)` branch ever references them.
// Only reference these inside `if (railsContext.serverSide)` — on the
// client they resolve to `undefined` (see comment above). Destructuring
// at module init outside the guard would throw at client load.
// eslint-disable-next-line import/no-unresolved
import { ChunkExtractor } from '@loadable/server'
// eslint-disable-next-line import/no-unresolved, import/extensions
import loadableStats from '@loadable-stats'
import {
  ApolloProvider,
  ApolloProvider as ApolloHooksProvider,
} from '@apollo/client'
import { HelmetProvider } from 'react-helmet-async'
import i18n from 'i18next'
import ahoy from 'ahoy.js'
import useDispatch from './hooks/useDispatch'

import configureApolloClient from './configureApolloClient'
import configureReduxStore from './typedRedux/configureStoreWithHistory'
import seedSearchResults from './search/seedSearchResults'
import seedCollection from './pages/collections/seedCollection'
import seedRegion from './pages/static/Region/seedRegion'
import styledTheme from './styles/theme'
import GlobalStyle from './styles/GlobalStyles'

import CreateNewBucketList from './pages/bucketlist/CreateNewBucketList'
import ToastNotifier from './components/notifications/ToastNotifier'
import ToastProvider from './components/notifications/ToastProvider'
import AuthModal from './components/auth/AuthModal'
import SelectEscapeModal from './components/SelectEscape/Modal'
import JoinBooking from './components/bookings/Join'

import RollbarErrorBoundary from './components/display/RollbarErrorBoundary'

import IsomorphicRoutes from './routes/IsomorphicRoutes'

import RailsContext, { RailsContextInterface } from './contexts/RailsContext'
import ServerSideContext from './contexts/ServerSideContext'
import TouchDeviceContext from './contexts/TouchDeviceContext'

import { isTouchDevice } from './components/utils/displayUtils'

import Navbar from './layout/Navbar'
import ScrollToTopOnSpaNav from './components/utils/ScrollToTopOnSpaNav'

import { fetchCurrentUser } from './actions/authActions'
import { fetchFeatureFlags } from './actions/featureFlagsActions'
import { useIdentifyUserOnSettle } from './analytics/useIdentifyUserOnSettle'
import { useIdentifyUserToServices } from './analytics/useIdentifyUserToServices'
import AccountVerificationBanner from './components/standalone/AccountVerificationBanner'
import VoucherBanner from './components/standalone/VoucherBanner'
import WildfiresBanner from './components/standalone/WildfiresBanner'
import { MediaQueryProvider } from './contexts/MediaQueryContext'
import EmailUnverified from './components/notifications/EmailUnverified'

import SetLanguage from './i18n/SetLanguage'
import { normalizeLocale } from './i18n'

type ApolloClientInstance = ReturnType<typeof configureApolloClient>

// Dispatched from inside the React tree via useEffect so it fires
// *after* hydration completes. Running these dispatches at module
// load (or via setTimeout) flips auth.loading from false (server
// snapshot) to true mid-hydration, which React 18 reports as a
// prop diff on buttons that bind `disabled` to auth.loading.
const InitialFetches = (): null => {
  const dispatch = useDispatch()

  useEffect(() => {
    // Mint the ahoy_visitor cookie before the features fetch so /api/features
    // evaluates against the visitor token even on the first cookieless pageview.
    ahoy.start()
    dispatch(fetchCurrentUser())
    dispatch(fetchFeatureFlags())
  }, [dispatch])

  useIdentifyUserOnSettle()
  useIdentifyUserToServices()

  return null
}

// isTouchDevice() reads from `window`, so on the server it always
// evaluates to false while the client may evaluate to true on mobile —
// a hydration mismatch that React 18 treats as a hard error. Defer to
// the server's value on first client render, then update after mount.
const TouchDeviceProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isTouch, setIsTouch] = useState(false)
  useEffect(() => setIsTouch(isTouchDevice()), [])
  return (
    <TouchDeviceContext.Provider value={isTouch}>
      {children}
    </TouchDeviceContext.Provider>
  )
}

// ClientRoutes is required only on the client (see renderApp), so on
// SSR the Switch's children are `undefined` and it renders nothing. To
// match that on the client's first render — and avoid React 18 #418 →
// #423 firing a full re-render of the App root — defer mounting the
// matched route until after hydration.
const ClientRoutesSwitch: FC<{ clientRoutes: ReactNode }> = ({
  clientRoutes,
}) => {
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])
  return <Switch>{hydrated ? clientRoutes : null}</Switch>
}

type ReduxStore = ReturnType<typeof configureReduxStore>['store']
type History = ReturnType<typeof configureReduxStore>['history']

const renderApp = (
  store: ReduxStore,
  history: History,
  railsContext: RailsContextInterface,
  client: ApolloClientInstance,
) => {
  let ClientRoutes
  const clientSide = !railsContext.serverSide
  if (clientSide) {
    // eslint-disable-next-line global-require
    ClientRoutes = require('./routes/ClientRoutes').default
  }

  return (
    <HelmetProvider>
      <RailsContext.Provider value={railsContext}>
        <ServerSideContext.Provider value={railsContext.serverSide}>
          <TouchDeviceProvider>
            <Provider store={store} key="provider">
              <InitialFetches />
              <ApolloProvider client={client}>
                <ApolloHooksProvider client={client}>
                  <MediaQueryProvider>
                    <ThemeProvider theme={styledTheme}>
                      <ToastProvider>
                        <SetLanguage>
                          <RollbarErrorBoundary name="app-root">
                            <EmailUnverified />
                            <Router history={history}>
                              <ScrollToTopOnSpaNav />
                              {!railsContext.embedded && <Navbar />}
                              <VoucherBanner />
                              <WildfiresBanner />
                              <GlobalStyle />
                              <div>
                                <AccountVerificationBanner />
                                <ToastNotifier />
                                <AuthModal />
                                <JoinBooking />
                                <SelectEscapeModal />
                                <CreateNewBucketList />
                                <ClientRoutesSwitch
                                  clientRoutes={ClientRoutes}
                                />
                                <Switch>{IsomorphicRoutes}</Switch>
                              </div>
                            </Router>
                          </RollbarErrorBoundary>
                        </SetLanguage>
                      </ToastProvider>
                    </ThemeProvider>
                  </MediaQueryProvider>
                </ApolloHooksProvider>
              </ApolloProvider>
            </Provider>
          </TouchDeviceProvider>
        </ServerSideContext.Provider>
      </RailsContext.Provider>
    </HelmetProvider>
  )
}

export default (props: any, railsContext: RailsContextInterface) => {
  // Sync i18next with the per-request locale before the tree is rendered.
  // Server: factory runs once per request inside mini_racer's shared V8
  // context, so this is the only safe place to mutate the singleton i18n.
  // Client: factory runs once before hydrateRoot, ensuring the first
  // hydration render matches the SSR HTML.
  i18n.changeLanguage(normalizeLocale(railsContext.i18nLocale))

  const { location, serverSide } = railsContext
  const { store, history } = configureReduxStore(props, {
    initialEntries: [location],
  })
  // ssrMode skips polling and prioritises cache over network during
  // server render. Without it Apollo 3.8+'s useSyncExternalStore-backed
  // useQuery executes the network path at SSR — fetch is a noop in our
  // mini_racer environment so HttpLink throws, react-on-rails returns an
  // error hash, and the app_component helper crashes on nil.
  const client = configureApolloClient({ ssrMode: serverSide })

  seedSearchResults(client, props?.rails?.data?.searchResults, location)
  seedCollection(
    client,
    props?.rails?.data?.collectionSsr,
    props?.collection?.data,
  )
  seedRegion(
    client,
    props?.rails?.data?.regionSsr,
    props?.rails?.data?.currentRegion,
    props?.rails?.data?.currentMarket,
  )

  const renderedApp = renderApp(store, history, railsContext, client)

  // fetchCurrentUser / fetchFeatureFlags are dispatched from
  // InitialFetches inside the App tree so they run after hydration.

  // SSR: collect styled-components styles and hand both pieces back to
  // Rails via react_component_hash so the styles can be emitted into
  // <head> instead of as a child of the React root. Keeping them in the
  // tree caused a structural child-count mismatch on hydration (React 18
  // #418/#425 -> #423 full client re-render) since the client wrapper
  // had no styles child.
  if (serverSide) {
    const sheet = new ServerStyleSheet()
    // entrypoints: [] tells ChunkExtractor to only include script tags
    // for async chunks pulled in by loadable() during render — the main
    // entrypoint's scripts are already emitted by the Rails layout via
    // webpack_entry_scripts.
    const extractor = new ChunkExtractor({
      stats: loadableStats,
      entrypoints: [],
    })
    try {
      const tree = sheet.collectStyles(extractor.collectChunks(renderedApp))
      const componentHtml = renderToString(tree)
      const componentStyles = sheet.getStyleTags()
      const loadableChunks = extractor.getScriptTags()
      return {
        renderedHtml: { componentHtml, componentStyles, loadableChunks },
      }
    } finally {
      // seal() removes the sheet from styled-components' module-level
      // registry. mini_racer reuses the V8 context across SSR requests,
      // so a throw without seal() would leak this sheet's collected
      // styles into subsequent renders.
      sheet.seal()
    }
  }

  return () => renderedApp
}
